// buildingFootprint.ts — Shared utility for computing building footprint from elevation chain
// Used by both SiteViewport3D (3D rendering) and FloorPlanCanvas (2D plan view)

import type { Elevation, PanelData } from '../types/panelvar';
import { cornerJointWidth, cornerTurnAngle, cornerThicknessOffset } from '../types/panelvar';

export interface Point2D {
  x: number;
  y: number;
}

export interface WallSegment2D {
  elevId: string;
  name: string;
  direction: string;
  panels: PanelData[];
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  angle: number; // radians, 0 = along +X
  leftEndJoint: number;
  rightEndJoint: number;
  totalWidth: number; // total wall width in inches
}

export interface FootprintResult {
  walls: WallSegment2D[];
  corners: Point2D[];
  isClosed: boolean;
}

/** Compute the total wall width (inches) for an elevation's panels + joints */
export function wallWidth(elevPanels: PanelData[], jointWidth: number, leftJoint: number, rightJoint: number): number {
  if (elevPanels.length === 0) return 0;
  let w = leftJoint;
  for (let i = 0; i < elevPanels.length; i++) {
    w += elevPanels[i].mpvar.width;
    if (i < elevPanels.length - 1) w += jointWidth;
  }
  w += rightJoint;
  return w;
}

/** Compute left/right edge trim (inches) for a panel within its elevation.
 *  Interior edges: jW/2 each side.  Corner edges: 0 for continuous/l-panel,
 *  full jW for butt/pilaster/gap, 0.375 for miter. */
export function computeEdgeTrims(
  panelId: string,
  elevation: Elevation,
  jointWidth: number,
): { leftTrim: number; rightTrim: number } {
  const idx = elevation.panelIds.indexOf(panelId);
  if (idx < 0) return { leftTrim: 0, rightTrim: 0 };
  const last = elevation.panelIds.length - 1;

  const leftTrim  = idx === 0    ? cornerJointWidth(elevation.leftCorner,  jointWidth) : jointWidth / 2;
  const rightTrim = idx === last  ? cornerJointWidth(elevation.rightCorner, jointWidth) : jointWidth / 2;
  return { leftTrim, rightTrim };
}

/** Average panel thickness for an elevation (used for butt-joint offsets) */
export function avgThickness(elevPanels: PanelData[]): number {
  if (elevPanels.length === 0) return 8;
  return elevPanels.reduce((s, p) => s + p.mpvar.thickness, 0) / elevPanels.length;
}

/** Direction-to-angle mapping: B = 0 (+X), R = 3π/2 (−Y), F = π (−X), L = π/2 (+Y) */
export const DIR_ANGLE: Record<string, number> = {
  B: 0,
  R: (3 * Math.PI) / 2,
  F: Math.PI,
  L: Math.PI / 2,
};

/**
 * Walk the elevation chain and compute a 2D building footprint.
 * Coordinate system: X = right/east, Y = up/north (plan view).
 * The 3D renderer maps: 3D-X → 2D-X, 3D-Z → 2D-Y (with sign flip in sine).
 */
export function computeFootprint(
  elevations: Elevation[],
  panels: PanelData[],
  jointWidth: number = 0.75,
): FootprintResult {
  if (elevations.length === 0) return { walls: [], corners: [], isClosed: false };

  const checkedIds = new Set(elevations.map(e => e.id));
  const byId = new Map(elevations.map(e => [e.id, e]));

  const resolvePanels = (elev: Elevation): PanelData[] =>
    elev.panelIds.map(id => panels.find(p => p.id === id)).filter((p): p is PanelData => p != null);

  // Find chain start: elevation whose leftConnectsTo is NOT in checked set
  let startElev: Elevation | undefined;
  for (const e of elevations) {
    if (!e.leftConnectsTo || !checkedIds.has(e.leftConnectsTo)) {
      startElev = e;
      break;
    }
  }
  if (!startElev) startElev = elevations[0];

  const walls: WallSegment2D[] = [];
  const corners: Point2D[] = [];
  const visited = new Set<string>();

  let curX = 0;
  let curY = 0;
  let curAngle = DIR_ANGLE[startElev.direction] ?? 0;

  let current: Elevation | undefined = startElev;
  while (current && !visited.has(current.id)) {
    visited.add(current.id);

    const ep = resolvePanels(current);
    const lj = cornerJointWidth(current.leftCorner ?? 'continuous', jointWidth);
    const rj = cornerJointWidth(current.rightCorner ?? 'continuous', jointWidth);
    const ww = wallWidth(ep, jointWidth, lj, rj);

    // Wall start corner
    corners.push({ x: curX, y: curY });

    // Compute wall end point
    const endX = curX + Math.cos(curAngle) * ww;
    const endY = curY - Math.sin(curAngle) * ww;

    walls.push({
      elevId: current.id,
      name: current.name,
      direction: current.direction,
      panels: ep,
      startX: curX,
      startY: curY,
      endX,
      endY,
      angle: curAngle,
      leftEndJoint: lj,
      rightEndJoint: rj,
      totalWidth: ww,
    });

    // Corner turn calculation
    const nextId: string | undefined = current.rightConnectsTo;
    const nextElev: Elevation | undefined = nextId && checkedIds.has(nextId) ? byId.get(nextId) : undefined;
    const rightType = current.rightCorner ?? 'continuous';
    const leftType = nextElev?.leftCorner ?? 'continuous';
    const effectiveTurnDeg = Math.max(cornerTurnAngle(rightType), cornerTurnAngle(leftType));

    // Determine turn direction from the terminating side's offset rule:
    //   interior (butt-int) → CW turn (negative angle)
    //   exterior (butt-ext) → CCW turn (positive angle)
    const rightOffset = cornerThicknessOffset(rightType);
    const leftOffset = cornerThicknessOffset(leftType);
    const offsetRule = rightOffset !== 'none' ? rightOffset : leftOffset;
    const turnSign = offsetRule === 'exterior' ? 1 : -1;
    const turnRad = turnSign * (effectiveTurnDeg * Math.PI) / 180;

    // Combined corner offset: each butt-joint corner shifts exactly T/2
    // both along the current wall and perpendicular to it.
    //   alongCoeff: continuous side backs up (-1), butt side advances (+1)
    //   perpCoeff:  interior→left-of-travel (+1), exterior→right (-1),
    //               flipped when current wall is the butt (terminating) side
    const nextAngle = curAngle + turnRad;
    let nextX = endX;
    let nextY = endY;
    if (offsetRule !== 'none' && effectiveTurnDeg !== 0) {
      const halfT = avgThickness(ep) / 2;
      const isCurContinuous = rightType === 'continuous';

      const alongCoeff = isCurContinuous ? -1 : 1;
      const perpSign = offsetRule === 'interior' ? 1 : -1;
      const perpCoeff = isCurContinuous ? perpSign : -perpSign;

      // alongVec = (cos θ, -sin θ), perpVec = (sin θ, cos θ)
      const ax = Math.cos(curAngle), ay = -Math.sin(curAngle);
      const px = Math.sin(curAngle), py = Math.cos(curAngle);
      nextX += alongCoeff * halfT * ax + perpCoeff * halfT * px;
      nextY += alongCoeff * halfT * ay + perpCoeff * halfT * py;
    }
    curAngle = nextAngle;
    curX = nextX;
    curY = nextY;
    current = nextElev;
  }

  const isClosed = current !== undefined && visited.has(current.id);

  // Closure correction: only adjust corners[] for the floor polygon outline.
  // Do NOT modify walls[] — the T/2 offsets at each butt-joint corner are real
  // geometry, not floating-point error.  In a U-shape with 8 butt-int corners the
  // accumulated T/2 shift is ~14.5" in X — distributing that across wall origins
  // shifts every segment start by up to 12.7", destroying corner alignment.
  // Confirmed via diagnostics 2026-04-09: dx=-14.50 dy=-2.00 on Arlington.
  if (isClosed && corners.length > 1) {
    const dx = curX - corners[0].x;
    const dy = curY - corners[0].y;
    if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) {
      for (let i = 1; i < corners.length; i++) {
        const f = i / corners.length;
        corners[i].x -= dx * f;
        corners[i].y -= dy * f;
      }
    }
  }

  return { walls, corners, isClosed };
}

/**
 * Compute an inset roof outline from the building footprint polygon.
 * The roof sits INSIDE the walls on a ledger — inset each edge inward by
 * the corresponding wall's average panel thickness.
 *
 * Algorithm: For each polygon edge, compute the inward unit normal, offset
 * the edge inward by the wall thickness, then intersect adjacent offset
 * edges to get the new corner points.
 *
 * @param footprint - Result from computeFootprint()
 * @returns Inset polygon points, or empty array if < 3 walls
 */
export function computeRoofOutline(footprint: FootprintResult): Point2D[] {
  const { walls, corners } = footprint;
  if (corners.length < 3) return [];

  // Centroid — used to determine which normal direction is "inward"
  let cx = 0, cy = 0;
  for (const c of corners) { cx += c.x; cy += c.y; }
  cx /= corners.length;
  cy /= corners.length;

  // Build per-edge inward offset distance = average panel thickness of that wall
  const thicknesses = walls.map(w => avgThickness(w.panels));

  const n = corners.length;

  // For each edge, compute the offset line (shifted inward by wall thickness).
  // Use centroid to pick the correct inward normal direction.
  interface OffsetLine {
    px: number; py: number;   // Point on the offset line
    dx: number; dy: number;   // Direction vector (unit length, same as edge)
  }
  const offsetLines: OffsetLine[] = [];

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const edgeDx = corners[j].x - corners[i].x;
    const edgeDy = corners[j].y - corners[i].y;
    const len = Math.sqrt(edgeDx * edgeDx + edgeDy * edgeDy);
    if (len < 0.001) {
      offsetLines.push(offsetLines.length > 0
        ? offsetLines[offsetLines.length - 1]
        : { px: corners[i].x, py: corners[i].y, dx: 1, dy: 0 });
      continue;
    }

    // Two candidate normals perpendicular to edge
    const n1x = -edgeDy / len, n1y = edgeDx / len;
    const n2x = edgeDy / len, n2y = -edgeDx / len;

    // Edge midpoint
    const mx = (corners[i].x + corners[j].x) / 2;
    const my = (corners[i].y + corners[j].y) / 2;

    // Pick the normal that points toward centroid (= inward)
    const dot1 = n1x * (cx - mx) + n1y * (cy - my);
    const dot2 = n2x * (cx - mx) + n2y * (cy - my);
    const nx = dot1 >= dot2 ? n1x : n2x;
    const ny = dot1 >= dot2 ? n1y : n2y;

    const t = thicknesses[i] ?? 8;
    offsetLines.push({
      px: corners[i].x + nx * t,
      py: corners[i].y + ny * t,
      dx: edgeDx / len,
      dy: edgeDy / len,
    });
  }

  // Intersect adjacent offset lines to get inset corners
  const insetCorners: Point2D[] = [];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const L1 = offsetLines[i];
    const L2 = offsetLines[j];

    // L1: P1 + t1 * D1,  L2: P2 + t2 * D2
    // Cross product of directions = determinant
    const det = L1.dx * L2.dy - L1.dy * L2.dx;

    if (Math.abs(det) < 1e-10) {
      // Parallel edges — use midpoint
      insetCorners.push({
        x: (L1.px + L2.px) / 2,
        y: (L1.py + L2.py) / 2,
      });
    } else {
      // Solve for t1: L1.P + t1*L1.D = L2.P + t2*L2.D
      const diffX = L2.px - L1.px;
      const diffY = L2.py - L1.py;
      const t1 = (diffX * L2.dy - diffY * L2.dx) / det;
      insetCorners.push({
        x: L1.px + t1 * L1.dx,
        y: L1.py + t1 * L1.dy,
      });
    }
  }

  return insetCorners;
}
